import { kebabCase, snakeCase } from 'lodash/fp';

import { formatDate, formatNumber } from 'dashboard/lib/utils/formatters';
import fieldMetas from 'dashboard/lib/workflows/fieldMetas';
import { RedactedObjectPath } from 'dashboard/models/event';
import { WorkflowFieldKey } from 'dashboard/models/workflow';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';
import {
  JsonSchema,
  JsonSchemaInteger,
  JsonSchemaNumber,
  JsonSchemaObject,
  JsonSchemaTypes,
} from 'lib/json-schema';
import { Iso8601DateString, Json } from 'lib/types';
import { assertNever } from 'lib/utils/types';

/**
 * Corresponds with the classes in app/lib/field/*.rb
 * Demodulized and underscored
 */
export enum FieldType {
  Array = 'array',
  Boolean = 'boolean',
  Choices = 'choices',
  Date = 'date',
  Datetime = 'datetime',
  EmailAddress = 'email_address',
  File = 'file',
  Hash = 'hash',
  Integer = 'integer',
  Json = 'json',
  MultiChoices = 'multi_choices',
  Number = 'number',
  String = 'string',
  /**
   * NOTE: please also add all relation fields to the RELATION_FIELDS constant.
   */
  DocumentGeneric = 'generic',
  DocumentGovernmentIdNfc = 'government_id_nfc',
  DocumentMobileDriversLicense = 'mobile_drivers_license',
  GovernmentId = 'government_id',
  Selfie = 'selfie',
}

/**
 * Fields that reference another model rather than holding a value.
 * This should include all subclasses of `Field::Relation`.
 */
export const RELATION_FIELDS = new Set([
  FieldType.DocumentGeneric,
  FieldType.DocumentGovernmentIdNfc,
  FieldType.DocumentMobileDriversLicense,
  FieldType.GovernmentId,
  FieldType.Selfie,
]);

export enum FieldSchemaType {
  Array = 'array',
  Boolean = 'boolean',
  Choices = 'choices',
  Date = 'date',
  Datetime = 'datetime',
  EmailAddress = 'email-address',
  File = 'file',
  Hash = 'hash',
  Integer = 'integer',
  Json = 'json',
  MultiChoices = 'multi-choices',
  Number = 'number',
  String = 'string',
  /**
   * NOTE: please also add all relation fields to the RELATION_FIELDS constant.
   */
  Relation = 'Field::Relation',
  /**
   * Not ideal because 'generic' is not very descriptive. Should migrate
   * fields serialization to not demodulize. But that's a breaking change.
   */
  DocumentGeneric = 'Field::Relation::Document::Generic',
  GovernmentId = 'Field::Relation::Document::GovernmentId',
  GovernmentIdNfc = 'Field::Relation::Document::GovernmentIdNfc',
  MobileDriversLicense = 'Field::Relation::Document::MobileDriversLicense',
  Selfie = 'Field::Relation::Selfie',
}

export const RELATION_FIELD_SCHEMA_TYPES: Set<FieldSchemaType> = new Set([
  FieldSchemaType.DocumentGeneric,
  FieldSchemaType.GovernmentId,
  FieldSchemaType.GovernmentIdNfc,
  FieldSchemaType.MobileDriversLicense,
  FieldSchemaType.Selfie,
]);

export type FieldValue =
  | string
  | string[]
  | boolean
  | number
  | { id: string; type: string }
  | Json
  | Field[]
  | { [key: string]: Field };

interface FieldBase {
  type: FieldType;
  value: FieldValue;
  redacted?: boolean;
}

export interface FieldArray<T extends Field = Field> extends FieldBase {
  type: FieldType.Array;
  value: T[];
}

export interface FieldBoolean extends FieldBase {
  type: FieldType.Boolean;
  value: boolean;
}

export interface FieldChoices extends FieldBase {
  type: FieldType.Choices;
  value: string;
}

export interface FieldDate extends FieldBase {
  type: FieldType.Date;
  value: Iso8601DateString;
}

export interface FieldDatetime extends FieldBase {
  type: FieldType.Datetime;
  value: Iso8601DateString;
}

export interface FieldEmailAddress extends FieldBase {
  type: FieldType.EmailAddress;
  value: string;
}

export interface FieldFile extends FieldBase {
  type: FieldType.File;
  value: {
    url: string;
    filename: string;
    'mime-type': string;
    'byte-size': number;
  } | null;
}

export interface FieldHash<T extends Record<string, Field> = Record<string, Field>>
  extends FieldBase {
  type: FieldType.Hash;
  value: T;
}

export interface FieldInteger extends FieldBase {
  type: FieldType.Integer;
  value: number;
}

export interface FieldJson extends FieldBase {
  type: FieldType.Json;
  value: Json;
}

export interface FieldMultiChoices extends FieldBase {
  type: FieldType.MultiChoices;
  value: string[];
}

export interface FieldNumber extends FieldBase {
  type: FieldType.Number;
  value: number;
}

export interface FieldString extends FieldBase {
  type: FieldType.String;
  value: string;
}

interface FieldDocumentGeneric extends FieldBase {
  type: FieldType.DocumentGeneric;
  value: {
    id: string;
    type: 'Document::Generic';
  };
}

interface FieldGovernmentId extends FieldBase {
  type: FieldType.GovernmentId;
  value: {
    id: string;
    type: 'Document::GovernmentId';
  };
}

interface FieldDocumentGovernmentIdNfc extends FieldBase {
  type: FieldType.DocumentGovernmentIdNfc;
  value: {
    id: string;
    type: 'Document::GovernmentIdNfc';
  };
}

interface FieldDocumentMobileDriversLicense extends FieldBase {
  type: FieldType.DocumentMobileDriversLicense;
  value: {
    id: string;
    type: 'Document::MobileDriversLicense';
  };
}

interface FieldSelfie extends FieldBase {
  type: FieldType.Selfie;
  value: {
    id: string;
    type: 'Selfie::CenterOnly' | 'Selfie::ProfileAndCenter' | 'Selfie::ConfigurablePoses';
  };
}

export type Field =
  | FieldArray
  | FieldBoolean
  | FieldChoices
  | FieldDate
  | FieldDatetime
  | FieldEmailAddress
  | FieldFile
  | FieldHash
  | FieldInteger
  | FieldJson
  | FieldMultiChoices
  | FieldNumber
  | FieldString
  | FieldDocumentGeneric
  | FieldDocumentGovernmentIdNfc
  | FieldDocumentMobileDriversLicense
  | FieldGovernmentId
  | FieldSelfie;

/**
 * Map of field name to record of field data.
 */
export type FieldsMap = Record<string, Field>;

export const FIELD_SCHEMA_TYPE_DISPLAY: Partial<Record<FieldSchemaType, string>> = {
  [FieldSchemaType.Array]: 'Array',
  [FieldSchemaType.Boolean]: 'Boolean',
  [FieldSchemaType.Choices]: 'Choices',
  [FieldSchemaType.Date]: 'Date',
  [FieldSchemaType.Datetime]: 'Datetime',
  [FieldSchemaType.EmailAddress]: 'Email Address',
  [FieldSchemaType.File]: 'File',
  [FieldSchemaType.Hash]: 'Hash',
  [FieldSchemaType.Integer]: 'Integer',
  [FieldSchemaType.Json]: 'JSON',
  [FieldSchemaType.Number]: 'Float',
  [FieldSchemaType.String]: 'String',
  [FieldSchemaType.MultiChoices]: 'Multiple Choices',
  [FieldSchemaType.DocumentGeneric]: 'Document',
  [FieldSchemaType.GovernmentId]: 'Government ID',
  [FieldSchemaType.GovernmentIdNfc]: 'Government ID NFC',
  [FieldSchemaType.MobileDriversLicense]: 'Mobile Driver’s License',
  [FieldSchemaType.Selfie]: 'Selfie',
};

export type FieldSchema =
  | FieldSchemaArray
  | FieldSchemaBoolean
  | FieldSchemaChoices
  | FieldSchemaDate
  | FieldSchemaDatetime
  | FieldSchemaEmailAddress
  | FieldSchemaDocumentGeneric
  | FieldSchemaFile
  | FieldSchemaGovernmentId
  | FieldSchemaGovernmentIdNfc
  | FieldSchemaHash
  | FieldSchemaInteger
  | FieldSchemaJson
  | FieldSchemaMultiChoices
  | FieldSchemaNumber
  | FieldSchemaSelfie
  | FieldSchemaString;

export interface FieldSchemaBase {
  key: string;
  label: string;
  type: FieldSchemaType;
  'default-value'?: any;
  'comparison-key'?: string;
  // FIXME: Remove overly permissing any
  config: FieldSchemaConfig & Record<string, any>;
}

export type FieldRedactionPolicy = 'none' | 'never';
export type FieldWritePolicy = 'none' | 'write_once';

export const FIELD_POLICY_OPTIONS: {
  'redaction-policy': Array<{ label: string; value: FieldRedactionPolicy }>;
  'write-policy': Array<{ label: string; value: FieldWritePolicy }>;
} = {
  'redaction-policy': [
    {
      label: 'None',
      value: 'none',
    },
    {
      label: 'Never',
      value: 'never',
    },
  ],
  'write-policy': [
    {
      label: 'None',
      value: 'none',
    },
    {
      label: 'Write Once',
      value: 'write_once',
    },
  ],
};

export type FieldSchemaConfig = {
  'redaction-policy'?: FieldRedactionPolicy;
  'write-policy'?: FieldWritePolicy;
  // NOTE: `archived-at` is deprecated, prefer `deactivated-at`
  'archived-at'?: Iso8601DateString | null;
  'deactivated-at'?: Iso8601DateString | null;
  'encrypt-value'?: boolean;
  computed?: boolean;
  required?: boolean;
};

export interface FieldSchemaJson extends FieldSchemaBase {
  type: FieldSchemaType.Json;
  config: FieldSchemaConfig & {
    'json-schema': string;
  };
}

export interface FieldSchemaArray extends FieldSchemaBase {
  type: FieldSchemaType.Array;
  config: FieldSchemaConfig & {
    'item-schema': FieldSchema;
  };
}

export interface FieldSchemaHash extends FieldSchemaBase {
  type: FieldSchemaType.Hash;
  config: FieldSchemaConfig & {
    'item-schemas': FieldSchema[];
  };
}

export interface FieldSchemaBoolean extends FieldSchemaBase {
  type: FieldSchemaType.Boolean;
  config: FieldSchemaConfig & {
    computed?: boolean;
  };
}

export interface FieldSchemaChoices extends FieldSchemaBase {
  type: FieldSchemaType.Choices;
  config: FieldSchemaConfig & {
    'allow-empty'?: boolean;
    options?: string[];
    'option-labels'?: string[];
  };
}

export interface FieldSchemaDate extends FieldSchemaBase {
  type: FieldSchemaType.Date;
}

export interface FieldSchemaDatetime extends FieldSchemaBase {
  type: FieldSchemaType.Datetime;
}

export interface FieldSchemaEmailAddress extends FieldSchemaBase {
  type: FieldSchemaType.EmailAddress;
}

export interface FieldSchemaFile extends FieldSchemaBase {
  type: FieldSchemaType.File;
}

export interface FieldSchemaInteger extends FieldSchemaBase {
  type: FieldSchemaType.Integer;
  config: FieldSchemaConfig & {
    min?: number;
    max?: number;
    computed?: boolean;
  };
}

export interface FieldSchemaMultiChoices extends FieldSchemaBase {
  type: FieldSchemaType.MultiChoices;
  config: FieldSchemaConfig & {
    'allow-empty'?: boolean;
    options?: string[];
    'option-labels'?: string[];
  };
}

export interface FieldSchemaNumber extends FieldSchemaBase {
  type: FieldSchemaType.Number;
  config: FieldSchemaConfig & {
    min?: number;
    max?: number;
    computed?: boolean;
  };
}

export interface DynamicOptionsFieldSchema extends FieldSchemaArray {
  config: FieldSchemaConfig & {
    'item-schema': FieldSchemaHash;
  };
}

export type StringSanitization = 'html';

export const STRING_SANITIZATION_OPTIONS: Array<{
  label: string;
  value: StringSanitization;
}> = [
  {
    label: 'HTML',
    value: 'html',
  },
];

export interface FieldSchemaString extends FieldSchemaBase {
  type: FieldSchemaType.String;
  config: FieldSchemaConfig & {
    'max-char-length'?: number;
    sanitize?: StringSanitization[];
    computed?: boolean;
  };
}

// Used by related objects. Would be nice if there could also be new types.
export interface FieldSchemaDocumentGeneric extends FieldSchemaBase {
  type: FieldSchemaType.DocumentGeneric;
}

export interface FieldSchemaGovernmentId extends FieldSchemaBase {
  type: FieldSchemaType.GovernmentId;
}

export interface FieldSchemaGovernmentIdNfc extends FieldSchemaBase {
  type: FieldSchemaType.GovernmentIdNfc;
}

export interface FieldSchemaMobileDriversLicense extends FieldSchemaBase {
  type: FieldSchemaType.MobileDriversLicense;
}
export interface FieldSchemaSelfie extends FieldSchemaBase {
  type: FieldSchemaType.Selfie;
}

export enum JsonSchemaCustomTypes {
  Date = 'date',
  Enum = 'enum',
  File = 'file',
  MultiEnum = 'multi-enum',
  GroupedEnum = 'grouped-enum',
  Relation = 'relation',
  Unknown = 'unknown',
}

export type JsonSchemaAllTypes = JsonSchemaTypes | JsonSchemaCustomTypes;
export const JsonSchemaAllTypes = {
  ...JsonSchemaTypes,
  ...JsonSchemaCustomTypes,
};

export const JsonSchemaStringifiableTypes = [
  JsonSchemaTypes.Boolean,
  JsonSchemaTypes.Integer,
  JsonSchemaTypes.Number,
  JsonSchemaTypes.String,
  JsonSchemaCustomTypes.Date,
  JsonSchemaCustomTypes.Enum,
];

export const JsonSchemaNumberTypes = [JsonSchemaAllTypes.Integer, JsonSchemaAllTypes.Number];
export const JsonSchemaStringTypes = [
  JsonSchemaAllTypes.String,
  JsonSchemaAllTypes.Enum,
  JsonSchemaAllTypes.GroupedEnum,
];

export const JsonSchemaTypeToFieldType: Partial<
  Record<JsonSchemaTypes | JsonSchemaCustomTypes, FieldSchemaType>
> = {
  [JsonSchemaAllTypes.String]: FieldSchemaType.String,
  [JsonSchemaAllTypes.Boolean]: FieldSchemaType.Boolean,
  [JsonSchemaAllTypes.File]: FieldSchemaType.File,
  [JsonSchemaAllTypes.Integer]: FieldSchemaType.Integer,
  [JsonSchemaAllTypes.Number]: FieldSchemaType.Number,
  [JsonSchemaAllTypes.Date]: FieldSchemaType.Date,
  [JsonSchemaAllTypes.Enum]: FieldSchemaType.Choices,
  [JsonSchemaAllTypes.MultiEnum]: FieldSchemaType.MultiChoices,
  [JsonSchemaAllTypes.GroupedEnum]: FieldSchemaType.Choices,
  [JsonSchemaAllTypes.Array]: FieldSchemaType.Array,
  [JsonSchemaAllTypes.Relation]: FieldSchemaType.Relation,
  [JsonSchemaAllTypes.Object]: FieldSchemaType.Hash,
};

export const FieldTypeToJsonSchemaType: Partial<
  Record<FieldSchemaType, JsonSchemaTypes | JsonSchemaCustomTypes>
> = Object.fromEntries(Object.entries(JsonSchemaTypeToFieldType).map((a) => a.reverse()));

export type ParsedField =
  | string
  | boolean
  | number
  | null
  | ParsedField[]
  | Record<string, unknown>;

export function parseField(field: Field | null | undefined): ParsedField {
  if (!field) {
    return null;
  }

  switch (field.type) {
    // Parsing differently if the field is a simple field. Nested fields don't get special formatting
    case FieldType.Boolean:
      if (field.value == null) {
        return null;
      }
      return field.value ? 'true' : 'false';

    case FieldType.Number:
    case FieldType.Integer:
      return field.value == null ? '' : formatNumber(field.value);
    default:
      return parseObjectField(field);
  }
}

function parseObjectField(field: Field): ParsedField {
  if (field.redacted) {
    return '<Redacted>';
  }
  switch (field.type) {
    case FieldType.Array:
      return field.value?.map(parseObjectField);
    case FieldType.Date:
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return formatDate(field.value);
    case FieldType.Datetime:
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return formatDate(field.value, DATE_AND_TIME_TZ_FORMAT);
    case FieldType.Hash:
      return Object.entries(field.value).reduce((acc, [k, v]) => {
        return {
          ...acc,
          [k]: parseObjectField(v),
        };
      }, {});

    // Primitive types
    case FieldType.Boolean:
      if (field.value == null) {
        return null;
      }
      return field.value;

    case FieldType.Number:
    case FieldType.Integer:
      return field.value == null ? '' : field.value;

    // Stringish types don't need special handling
    // Special handling not yet implemented for addresses
    case FieldType.Choices:
    case FieldType.EmailAddress:
    case FieldType.String:
      return field.value;

    // Arrayish types don't need special handling
    // Consumers should render them in whatever way makes sense
    case FieldType.MultiChoices:
      return field.value;
    case FieldType.Json:
      return field.value != null ? JSON.stringify(field.value) : null;
    case FieldType.File:
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return field.value?.url;
    // Don't render relation fields
    case FieldType.DocumentGeneric:
    case FieldType.DocumentGovernmentIdNfc:
    case FieldType.DocumentMobileDriversLicense:
    case FieldType.GovernmentId:
    case FieldType.Selfie:
      return null;
    default:
      assertNever(field);
  }
}

export const convertFieldSchemasToJsonSchema = (fieldSchemas: FieldSchema[]): JsonSchemaObject => {
  return {
    type: JsonSchemaTypes.Object,
    properties: fieldSchemas.reduce<Record<string, JsonSchema>>((properties, fieldSchema) => {
      const { key } = fieldSchema;
      properties[key] = getJsonSchemaForFieldSchema(fieldSchema);
      return properties;
    }, {}),
  };
};

export const getFieldSchemaDescription = (fieldSchemaKey: string) => {
  return fieldMetas[kebabCase(fieldSchemaKey) as WorkflowFieldKey]?.description;
};

export const getJsonSchemaForFieldSchema = (fieldSchema: FieldSchema): JsonSchema => {
  switch (fieldSchema.type) {
    case FieldSchemaType.Array:
      return {
        type: JsonSchemaTypes.Array,
        description: getFieldSchemaDescription(fieldSchema.key),
        fieldType: fieldSchema.type,
        items: getJsonSchemaForFieldSchema(fieldSchema.config['item-schema']),
      };
    case FieldSchemaType.Choices:
      return {
        type: JsonSchemaTypes.String,
        enum: fieldSchema.config.options || [],
        labels: fieldSchema.config['option-labels'] || [],
        description: getFieldSchemaDescription(fieldSchema.key),
        fieldType: fieldSchema.type,
      };
    case FieldSchemaType.String:
      return {
        type: JsonSchemaTypes.String,
        description: getFieldSchemaDescription(fieldSchema.key),
        fieldType: fieldSchema.type,
        maxLength: fieldSchema.config['max-char-length'],
      };
    case FieldSchemaType.Boolean:
      return {
        type: JsonSchemaTypes.Boolean,
        description: getFieldSchemaDescription(fieldSchema.key),
        fieldType: fieldSchema.type,
      };
    case FieldSchemaType.Date:
    case FieldSchemaType.Datetime:
      return {
        type: JsonSchemaTypes.String,
        description: getFieldSchemaDescription(fieldSchema.key),
        fieldType: fieldSchema.type,
        format: 'date',
      };
    case FieldSchemaType.EmailAddress:
      return {
        type: JsonSchemaTypes.String,
        description: getFieldSchemaDescription(fieldSchema.key),
        fieldType: FieldSchemaType.EmailAddress,
        format: 'email',
      };
    case FieldSchemaType.File:
      return {
        type: JsonSchemaTypes.Object,
        description: getFieldSchemaDescription(fieldSchema.key),
        fieldType: fieldSchema.type,
        properties: {
          id: {
            type: JsonSchemaTypes.String,
          },
          storage: {
            type: JsonSchemaTypes.String,
          },
          url: {
            type: JsonSchemaTypes.String,
          },
          metadata: {
            type: JsonSchemaTypes.Object,
            properties: {
              filename: {
                type: JsonSchemaTypes.String,
              },
              size: {
                type: JsonSchemaTypes.Integer,
              },
              mime_type: {
                type: JsonSchemaTypes.Array,
                uniqueItems: true,
                items: {
                  type: JsonSchemaTypes.String,
                  enum: fieldSchema.config['supported-mime-types'] || [],
                },
              },
            },
          },
        },
      };
    case FieldSchemaType.Hash:
      return {
        ...convertFieldSchemasToJsonSchema(fieldSchema.config['item-schemas']),
        description: getFieldSchemaDescription(fieldSchema.key),
        fieldType: fieldSchema.type,
      };
    case FieldSchemaType.Integer: {
      const intJsonSchema: JsonSchemaInteger = {
        type: JsonSchemaTypes.Integer,
        description: getFieldSchemaDescription(fieldSchema.key),
        fieldType: fieldSchema.type,
      };
      if ('min' in fieldSchema.config) {
        intJsonSchema.minimum = fieldSchema.config.min;
      }
      if ('max' in fieldSchema.config) {
        intJsonSchema.maximum = fieldSchema.config.max;
      }
      return intJsonSchema;
    }
    case FieldSchemaType.Number: {
      const numberJsonSchema: JsonSchemaNumber = {
        type: JsonSchemaTypes.Number,
        description: getFieldSchemaDescription(fieldSchema.key),
        fieldType: fieldSchema.type,
      };
      if ('min' in fieldSchema.config) {
        numberJsonSchema.minimum = fieldSchema.config.min;
      }
      if ('max' in fieldSchema.config) {
        numberJsonSchema.maximum = fieldSchema.config.max;
      }
      return numberJsonSchema;
    }
    case FieldSchemaType.Json: {
      const jsonSchema = fieldSchema.config['json-schema'];
      const parsedJsonSchema = typeof jsonSchema === 'string' ? JSON.parse(jsonSchema) : jsonSchema;
      // NB: intentionally don't add fieldType here since we infer the actual type
      // based on the nested json schema
      // TODO @platform - replace all usage of Field::JSON with persona fields
      return {
        ...parsedJsonSchema,
        description: getFieldSchemaDescription(fieldSchema.key),
      };
    }
    case FieldSchemaType.MultiChoices:
      return {
        type: JsonSchemaTypes.Array,
        description: getFieldSchemaDescription(fieldSchema.key),
        fieldType: fieldSchema.type,
        uniqueItems: true,
        items: {
          type: JsonSchemaTypes.String,
          enum: fieldSchema.config.options || [],
          labels: fieldSchema.config['option-labels'] || [],
        },
      };
    case FieldSchemaType.DocumentGeneric:
    case FieldSchemaType.GovernmentId:
    case FieldSchemaType.GovernmentIdNfc:
    case FieldSchemaType.Selfie:
      return {
        type: JsonSchemaTypes.Object,
        description: getFieldSchemaDescription(fieldSchema.key),
        properties: {},
        fieldType: fieldSchema.type,
      };
    default:
      return {};
  }
};

export const isComputedField = (
  schema: FieldSchema,
): schema is FieldSchemaBoolean | FieldSchemaInteger | FieldSchemaNumber | FieldSchemaString => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return (
    (schema.type === FieldSchemaType.Boolean ||
      schema.type === FieldSchemaType.Integer ||
      schema.type === FieldSchemaType.Number ||
      schema.type === FieldSchemaType.String) &&
    schema.config.computed
  );
};

export const fillRedacted = (
  fieldsMap: FieldsMap,
  redactedObjectPaths: RedactedObjectPath[],
): {
  [x: string]: Field & { redacted: boolean };
} => {
  const redactedPaths = redactedObjectPaths
    .filter((rop) => rop.path.startsWith('fields.'))
    .map(({ path }) => snakeCase(path.split('fields.')[1]));
  return Object.keys(fieldsMap).reduce(
    (newFieldsMap, fieldKey) => ({
      ...newFieldsMap,
      [fieldKey]: {
        ...fieldsMap[fieldKey],
        redacted: redactedPaths.includes(fieldKey),
      },
    }),
    {},
  );
};

const containsFieldSchemaWithTypeAndKey = (
  fieldSchema: FieldSchema[],
  type: FieldSchemaType,
  key: string,
): boolean => {
  return fieldSchema.some((schema) => schema.type === type && schema.key === key);
};

/**
 * Given an array of field schema, returns true if it's a dynamic field item schema.
 * A hash is considered to be a dynamic field item schema if it fulfills one of the following conditions:
 * - contains a string field with key 'value' and a string field with key 'text'
 * - contains a field with key 'value' and a string field with key 'text' and a string field with key 'key'
 * See: app/models/inquiry/ui_component/input_select.rb:2
 */
const isDynamicFieldItemSchema = (fieldSchema: FieldSchema[]): boolean => {
  if (containsFieldSchemaWithTypeAndKey(fieldSchema, FieldSchemaType.String, 'value')) {
    return containsFieldSchemaWithTypeAndKey(fieldSchema, FieldSchemaType.String, 'text');
  } else if (fieldSchema.some((schema) => schema.key === 'value')) {
    return (
      containsFieldSchemaWithTypeAndKey(fieldSchema, FieldSchemaType.String, 'text') &&
      containsFieldSchemaWithTypeAndKey(fieldSchema, FieldSchemaType.String, 'key')
      // key is only required if value is not of type string
    );
  }
  return false;
};

// Given a field schema, returns true if it's an array of hashes, where each hash is a dynamic field item schema.
export const isDynamicFieldOption = (fieldSchema: FieldSchema): boolean => {
  return (
    fieldSchema.type === FieldSchemaType.Array &&
    fieldSchema.config['item-schema'].type === FieldSchemaType.Hash &&
    isDynamicFieldItemSchema(fieldSchema.config['item-schema'].config['item-schemas'])
  );
};

export const getDynamicFieldOptionValueSchema = (
  fieldSchema: DynamicOptionsFieldSchema,
): FieldSchema | undefined => {
  if (isDynamicFieldOption(fieldSchema)) {
    const valueItemSchema = fieldSchema.config['item-schema'].config['item-schemas'].find(
      (schema) => schema.key === 'value',
    );
    return valueItemSchema;
  }
};

type FieldSchemaWithChoices = Extract<
  FieldSchema,
  { type: FieldSchemaType.Choices | FieldSchemaType.MultiChoices }
>;

export const isFieldSchemaWithChoices = (
  field: FieldSchema | undefined,
): field is FieldSchemaWithChoices => {
  // safety-check for null
  if (field == null) return false;

  switch (field.type) {
    case FieldSchemaType.Choices:
    case FieldSchemaType.MultiChoices:
      return true;
    default:
      return false;
  }
};

export interface FlattenedField {
  value: string;
  active: boolean;
  type: FieldSchemaType;
}

function flattenFieldSchemaHelper(
  prefix: string,
  schemas: FieldSchema[],
  parentIsArray: boolean,
  shouldIncludeArrays: boolean,
  enableJsonParsingForArrays: boolean,
): FlattenedField[] {
  const flattenedFieldSchema: FlattenedField[] = [];

  schemas.forEach((schema: FieldSchema) => {
    // When the parent field is an array, we don't want to include the
    // child's key, which is meaningless, because we access array children
    // by index not by name.
    const key = parentIsArray ? '' : schema.key || '';

    if (schema.type === FieldSchemaType.Hash) {
      const keySchemas = flattenFieldSchemaHelper(
        prefix + key + '.',
        schema.config['item-schemas'],
        false,
        shouldIncludeArrays,
        enableJsonParsingForArrays,
      );
      if (enableJsonParsingForArrays && parentIsArray) {
        // This field schema represents an array of hash types. For hashes we want to
        // include the array of hashes itself as a field.
        flattenedFieldSchema.push({
          value: prefix + key,
          active: schema.config?.['deactivated-at'] == null,
          type: FieldSchemaType.Json,
        });
      }
      flattenedFieldSchema.push(...keySchemas);
    } else if (schema.type === FieldSchemaType.Array) {
      if (shouldIncludeArrays) {
        let itemSchema: FieldSchema[] = [schema.config['item-schema']];
        if (enableJsonParsingForArrays) {
          // For arrays where JSON parsing is allowed, we indicate that the type
          // is JSON.
          itemSchema = [
            {
              ...schema.config['item-schema'],
              type: FieldSchemaType.Json,
              config: {
                'json-schema': '',
              },
            },
          ];
        }
        const result = flattenFieldSchemaHelper(
          prefix + key + '[]',
          itemSchema,
          true,
          shouldIncludeArrays,
          enableJsonParsingForArrays,
        );
        flattenedFieldSchema.push(...result);
      }
    } else {
      flattenedFieldSchema.push({
        value: prefix + key,
        active: schema.config?.['deactivated-at'] == null,
        type: schema.type,
      });
    }
  });

  return flattenedFieldSchema;
}

export function flattenFieldSchema(
  schemas: FieldSchema[],
  shouldIncludeArrays: boolean = true,
  enableJsonParsingForArrays: boolean = false,
): FlattenedField[] {
  return flattenFieldSchemaHelper(
    '',
    schemas,
    false,
    shouldIncludeArrays,
    enableJsonParsingForArrays,
  );
}
